<template>
  <div>
    <send-modal :visible="visibleSendModal" @close="visibleSendModal = false" @send="sendEmail" />

    <div class="lifecycle-step" :class="stepClasses">
      <div class="lifecycle-marker" :style="{ background: step.statusColor }"></div>
      <div
        v-if="!isLast"
        class="lifecycle-connector"
        :class="{ 'lifecycle-connector--completed': step.connectorCompleted }"
      ></div>

      <div class="lifecycle-content">
        <div class="lifecycle-header">
          <div class="flex align-items-center gap-2">
            <h4 class="lifecycle-title">{{ step.title }}</h4>
            <a-tag v-if="step.showStatus && step.statusTitle" class="m-0" :color="step.statusColor">
              {{ step.statusTitle }}
            </a-tag>
          </div>
          <span v-if="step.date" class="lifecycle-date">{{ step.date }}</span>
        </div>

        <p v-if="step.description" class="lifecycle-description">{{ step.description }}</p>

        <div v-if="step.staticActions.length" class="lifecycle-actions">
          <button
            v-for="action in step.staticActions"
            :key="action.key"
            type="button"
            class="lifecycle-action-card"
            @click="runAction(action)"
          >
            {{ action.label }}
          </button>
        </div>

        <div v-if="showProgress" class="lifecycle-progress">
          <div class="lifecycle-progress-track">
            <div
              class="lifecycle-progress-bar"
              :style="{ width: `${progressPercent}%`, background: step.progressColor }"
            ></div>
          </div>
        </div>

        <button v-if="step.events.length" type="button" class="lifecycle-toggle" @click="expanded = !expanded">
          <a-icon :type="expanded ? 'down' : 'right'" />
          {{ eventsLabel }}
        </button>

        <template v-if="expanded && step.events.length">
          <div class="lifecycle-events">
            <div
              v-for="event in visibleEvents"
              :key="event.key"
              type="button"
              class="lifecycle-event"
              :class="{ 'lifecycle-event--highlighted': event.highlighted }"
            >
              <div class="lifecycle-event-info">
                <div class="lifecycle-event-heading">
                  <span class="lifecycle-event-name">{{ event.name }}</span>
                  <a-tag v-if="event.statusTitle" class="m-0" :color="event.statusColor">
                    {{ event.statusTitle }}
                  </a-tag>
                </div>
                <span v-if="event.description" class="lifecycle-event-description">{{ event.description }}</span>
                <span v-if="event.meta" class="lifecycle-event-meta">{{ event.meta }}</span>
              </div>
              <div v-if="event.actions.length" class="lifecycle-event-side">
                <button
                  v-for="action in event.actions"
                  :key="action.key"
                  class="lifecycle-action-card"
                  @click="runAction(action, event)"
                >
                  {{ action.label }}
                </button>
              </div>
            </div>
          </div>

          <button v-if="canShowMore" type="button" class="lifecycle-show-more" @click="showMoreEvents">
            Show more {{ hiddenEventsCount }}
          </button>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { Icon, Tag } from 'ant-design-vue'
import SendModal from '@/components/send-modal.vue'

export default {
  name: 'ProfileLifecycleEventStep',
  components: {
    'a-icon': Icon,
    'a-tag': Tag,
    'send-modal': SendModal
  },
  props: {
    step: {
      type: Object,
      required: true
    },
    isLast: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      expanded: false,
      eventsLimit: 20,
      eventsPerPage: 20,
      visibleSendModal: false,
      pendingEmailAction: undefined,
      pendingEmailEvent: undefined
    }
  },
  computed: {
    visibleEvents() {
      return this.step.events.slice(0, this.eventsLimit)
    },

    canShowMore() {
      return this.visibleEvents.length < this.step.events.length
    },

    hiddenEventsCount() {
      return this.step.events.length - this.visibleEvents.length
    },

    showProgress() {
      return this.step.showProgress && this.step.events.length > 0 && this.step.expectedEventsCount > 0
    },

    progressPercent() {
      if (!this.step.expectedEventsCount) return 0

      return Math.min(Math.round((this.completedEventsCount / this.step.expectedEventsCount) * 100), 100)
    },

    completedEventsCount() {
      return Number.isFinite(this.step.completedEventsCount) ? this.step.completedEventsCount : this.step.events.length
    },

    eventsLabel() {
      const count = this.step.events.length
      const noun = count === 1 ? 'event' : 'events'

      return `${count} ${noun}`
    },

    stepClasses() {
      return {
        'lifecycle-step--completed': this.step.completed,
        'lifecycle-step--current': this.step.current,
        'lifecycle-step--danger': this.step.danger,
        'lifecycle-step--muted': this.step.muted
      }
    }
  },
  watch: {
    expanded(newVal) {
      if (!newVal) {
        this.eventsLimit = this.eventsPerPage
      }
    },

    'step.events.length'() {
      this.eventsLimit = this.eventsPerPage
    }
  },
  methods: {
    showMoreEvents() {
      this.eventsLimit += this.eventsPerPage
    },

    runAction(action, event) {
      if (action.key === 'send-export') {
        this.pendingEmailAction = action
        this.pendingEmailEvent = event
        this.visibleSendModal = true

        return
      }

      this.$emit('run-action', { action, event })
    },

    sendEmail(email) {
      this.visibleSendModal = false

      this.$emit('run-action', {
        action: {
          ...this.pendingEmailAction,
          email
        },
        event: this.pendingEmailEvent
      })

      this.pendingEmailAction = undefined
      this.pendingEmailEvent = undefined
    }
  }
}
</script>

<style scoped>
.lifecycle-step {
  position: relative;
  display: flex;
  gap: 20px;
  color: var(--text-color);
}

.lifecycle-step::before {
  display: none;
}

.lifecycle-connector {
  position: absolute;
  left: 7px;
  top: 20px;
  bottom: -22px;
  width: 1px;
  background: #10b981;
}

.lifecycle-marker {
  position: relative;
  z-index: 2;
  width: 15px;
  height: 15px;
  flex: 0 0 15px;
  margin-top: 4px;
  border-radius: 50%;
  background: #6b7280;
  border: 2px solid var(--bg-component);
}

.lifecycle-step--completed .lifecycle-marker {
  background: #10b981;
}

.lifecycle-step--current .lifecycle-marker {
  background: #f59e0b;
  box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.25);
}

.lifecycle-step--danger .lifecycle-marker {
  background: #ef4444;
}

.lifecycle-step--muted {
  opacity: 0.55;
}

.lifecycle-content {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.lifecycle-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.lifecycle-title {
  margin: 0;
  font-size: 15px;
}

.lifecycle-date,
.lifecycle-description,
.lifecycle-event-description,
.lifecycle-event-meta {
  color: var(--text-color-secondary);
}

.lifecycle-description {
  margin: 4px 0 8px;
}

.lifecycle-progress {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.lifecycle-progress-track {
  height: 3px;
  overflow: hidden;
  background: var(--border-color-base);
  border-radius: 999px;
}

.lifecycle-progress-bar {
  height: 100%;
  background: var(--primary-color);
  border-radius: 999px;
  transition: width 0.2s ease;
}

.lifecycle-progress-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  color: var(--text-color-secondary);
  font-size: 12px;
  font-weight: 600;
}

.lifecycle-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.lifecycle-toggle {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 34px;
  padding: 0 14px;
  color: var(--text-color);
  background: var(--bg-component);
  border: 1px solid var(--border-color-base);
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;
  width: fit-content;
}

.lifecycle-events {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 360px;
  overflow-y: auto;
  padding-right: 4px;
}

.lifecycle-event {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  padding: 10px 12px;
  text-align: left;
  color: var(--text-color);
  background: var(--bg-component);
  border: 1px solid var(--border-color-base);
  border-radius: 6px;
}

.lifecycle-event--highlighted {
  border-color: var(--primary-color);
  box-shadow: 0 0 0 1px rgba(24, 144, 255, 0.12);
}

.lifecycle-event-info {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.lifecycle-event-heading {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.lifecycle-event-name {
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.lifecycle-event-side {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.lifecycle-action-card {
  min-height: 32px;
  padding: 0 12px;
  color: var(--primary-color);
  background: var(--bg-component);
  border: 1px solid var(--primary-color);
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;
  white-space: nowrap;
}

.lifecycle-show-more {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 32px;
  margin-top: 8px;
  padding: 0 12px;
  color: var(--primary-color);
  background: transparent;
  border: 1px solid var(--border-color-base);
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;
}

@media screen and (max-width: 640px) {
  .lifecycle-header,
  .lifecycle-event {
    align-items: flex-start;
    flex-direction: column;
  }
}
</style>
